import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { escapeRegExp, isEqual } from 'lodash';
import { ScrollbarComponent } from 'ngx-scrollbar';
import { ClassNames } from '../../../constants/class-names.constants';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { ISelectOption } from '../../../models/common';
import { Utilities } from '../../../utilities/utilities.helper';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * LSQ Select
 * @group Forms
 * @component Select
 *
 * @description Renders a select dropdown
 * @export
 * @class LsqSelectComponent
 * @extends {BaseFormControlComponent<any>}
 * @implements {OnInit}
 * @property {Array<string|number|SelectOption>} options An array of primitive values or objects with the shape `{ label: string, value: any, options: SelectOption[] }`.
 * @property {string} label Renders a label.
 * @property {boolean} [floatLabel] If `true`, places the label above the main select box text. Defaults to `true`.
 * @property {boolean} [innerLabel] If `true`, places the label inside the viewport above the main select box text. Defaults to `false`.
 * @property {boolean} [isNullable] If `true`, displays '----' as the null option. Defaults to `false`.
 * @property {boolean} [arrow] If set to `false`, will not display the down-arrow icon on the select box. Defaults to `true`.
 * @property {boolean} [caret] If set to `true`, will display the caret icon above the dropdown menu. Defaults to `false`.
 * @property {string} [parentClass] Adds to the `class` on the `.select-group` div. Add 'sm' to style a narrow select box (8rem width) or 'lg' to style a wide one (31.2rem); add 'no-triangle' to hide the little triangle at the top of the dropdown.
 * @property {string} [inputClass] Adds to the `class` on the select input.
 * @property {string} [labelClass] Adds to the `class` on the label.
 * @property {boolean} [hideLabel] Makes the label invisible except to screenreaders.
 * @property {'dark'|'light'} [border] Defines the color of the border. Defaults to `'light'`.
 * @property {boolean} [dropUp] If `true`, the dropdown will display above the select input.
 * @property {boolean} [compact] If `true`, the viewport will have the compact styling.
 */

@Component({
    selector: 'lsq-select',
    template: `
        <div
            class="form-group select-group"
            [ngClass]="[
                parentClass,
                basicLabel ? 'has-basic-label' : '',
                placeholder ? 'has-placeholder' : '',
                innerLabel ? 'has-inner-label' : '',
                innerLeftLabel ? 'has-inner-left-label' : '',
                floatLabel ? 'has-float-label' : '',
                dropUp ? 'drop-up' : '',
                !compact ? 'form-group select-group' : ''
            ]"
        >
            <div
                class="lsq-select-input"
                #selectElement
                [ngClass]="{
                    'is-expanded': isExpanded,
                    disabled: disabled,
                    compact: compact
                }"
                [id]="createId()"
                [attr.tabindex]="'0'"
                (keydown)="handleViewportKeypress($event)"
                (keypress)="doSearch($event)"
                (focus)="handleFocus()"
                (blur)="handleBlur()"
                (click)="clearSearch()"
            >
                <label
                    *ngIf="label && basicLabel"
                    class="basic-label"
                    [attr.for]="createId()"
                    [ngClass]="labelClass"
                    (click)="toggleOptionsDropdown()"
                    >{{ label }}</label
                >

                <div
                    *ngIf="!compact"
                    class="form-control input-group lsq-select-viewport"
                    [ngClass]="[
                        viewportClass,
                        inputClass,
                        isFilledClass(),
                        isFocused ? 'is-focused' : '',
                        'combined-' + combinedStyle
                    ]"
                    aria-live="polite"
                    (click)="toggleOptionsDropdown()"
                >
                    <label
                        *ngIf="label && innerLeftLabel && !basicLabel"
                        [attr.for]="createId()"
                        [ngClass]="labelClass"
                        (click)="toggleOptionsDropdown()"
                        >{{ label }}</label
                    >

                    <span
                        *ngIf="displayNullOptionSelected() && !placeholder"
                        class="lsq-select-viewport-text null-option-selected"
                    >
                        {{ label }}
                    </span>

                    <span
                        *ngIf="displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        *ngIf="!isFilledClass() && !displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        class="lsq-select-viewport-text text-truncate"
                        *ngIf="displaySelectedOption()"
                        >{{
                            options[selectedOptionIndex]?.selectedLabel
                                ? options[selectedOptionIndex]?.selectedLabel
                                : options[selectedOptionIndex]?.label
                        }}</span
                    >

                    <lsq-icon
                        *ngIf="arrow"
                        class="chevron-icon"
                        [ngClass]="{
                            black: !hasDarkBackground(),
                            white: hasDarkBackground()
                        }"
                        [type]="icons.ChevronDown"
                        [color]="hasDarkBackground() ? color.White : color.Black"
                    >
                    </lsq-icon>
                </div>

                <div
                    *ngIf="compact"
                    class="lsq-select-input-compact"
                    (click)="toggleOptionsDropdown()"
                >
                    <span
                        *ngIf="displayNullOptionSelected() && !placeholder"
                        class="lsq-select-viewport-text null-option-selected"
                    >
                        {{ label }}
                    </span>

                    <span
                        *ngIf="displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        *ngIf="!isFilledClass() && !displayNullOptionSelected() && !!placeholder"
                        class="lsq-select-viewport-text null-option-selected text-gray-600"
                    >
                        {{ placeholder }}
                    </span>

                    <span
                        class="lsq-select-viewport-text text-truncate"
                        *ngIf="displaySelectedOption()"
                    >
                        {{
                            options[selectedOptionIndex]?.selectedLabel
                                ? options[selectedOptionIndex]?.selectedLabel
                                : options[selectedOptionIndex]?.label
                        }}
                    </span>

                    <div class="triangle" [ngClass]="{ up: isExpanded, down: !isExpanded }"></div>
                </div>

                <label
                    *ngIf="label && !innerLeftLabel && !basicLabel"
                    [attr.for]="createId()"
                    [ngClass]="labelClass"
                    (click)="toggleOptionsDropdown()"
                >
                    {{ label }}
                </label>

                <div
                    *ngIf="isExpanded"
                    class="lsq-dropdown-container lsq-select-options-dropdown-container"
                    [ngClass]="{
                        'no-label': basicLabel && (label == null || label === ''),
                        'no-caret': !caret,
                        compact: compact
                    }"
                >
                    <span
                        *ngIf="caret"
                        class="dropdown-triangle"
                        [ngClass]="{ bottom: dropUp }"
                    ></span>
                    <div class="lsq-select-scroll-container" style="overflow: auto;">
                        <ng-scrollbar #scrollBarRef>
                            <ul #dropdownMenuRef class="lsq-select-options-dropdown list-unstyled">
                                <li
                                    #optionRefs
                                    *ngFor="let option of options; let i = index"
                                    class="lsq-select-option"
                                    (click)="handleSelectChange(i, true, true)"
                                    [title]="option.titleText ? option.titleText : ''"
                                    [ngClass]="{
                                        selected: i === highlightedOptionIndex,
                                        'null-option': option.value === undefined
                                    }"
                                >
                                    <span *ngIf="option.isChild">&nbsp;&nbsp;&nbsp;</span>
                                    <span
                                        class="lsq-select-option-text lsq-select-option-null-text"
                                        *ngIf="isNullOption(i)"
                                        >----</span
                                    >
                                    <span
                                        class="lsq-select-option-text"
                                        *ngIf="isNotNullOption(i)"
                                        >{{ option.label }}</span
                                    >
                                    <h5
                                        class="lsq-select-option-sub-text"
                                        *ngIf="option.subLabel && isNotNullOption(i)"
                                    >
                                        {{ option.subLabel }}
                                    </h5>
                                </li>
                            </ul>
                        </ng-scrollbar>
                    </div>
                </div>
            </div>
            <h6
                class="input-error-message text-uppercase font-weight-bold"
                *ngIf="showErrorMessage()"
            >
                {{ getErrorMessage() }}
            </h6>
        </div>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqSelectComponent),
            multi: true
        }
    ]
})
export class LsqSelectComponent extends BaseFormControlComponent<any>
    implements OnInit, ControlValueAccessor {
    @Input() public options: ISelectOption[] = [];
    @Input() public label: string;
    @Input() public name?: string;
    @Input() public placeholder: string;
    @Input() public compact = false;
    @Input() public backgroundColor?: string;
    @Input() public basicLabel = false;
    @Input() public floatLabel = true;
    @Input() public innerLabel = false;
    @Input() public innerLeftLabel = false;
    @Input() public isNullable = false;
    @Input() public arrow = true;
    @Input() public caret = false;
    @Input() public parentClass = '';
    @Input() public labelClass = '';
    @Input() public inputClass = '';
    @Input() public hideLabel = false;
    @Input() public border?: 'dark' | 'light' = 'light';
    @Input() public combinedStyle?: 'left' | 'right' | 'none' = 'none';
    @Input() public dropUp = false;
    @Input() public disabled = false;
    @Input() public class = '';
    @Output() public blur = new EventEmitter<Event>();
    @ViewChild('selectElement') public selectElement: ElementRef;
    @ViewChild('dropdownMenuRef') public dropdownMenuRef: ElementRef;
    @ViewChildren('optionRefs') public optionRefs: QueryList<ElementRef>;
    @ViewChild('scrollBarRef') public scrollRef: ScrollbarComponent;

    public selectedOptionIndex = 0;
    public isExpanded = false;
    public search = '';
    public clearSearchTimer: number;
    public viewportClass = '';
    public isFocused = false;
    public icons = Icons;
    public color = Color;

    public ngOnInit(): void {
        this.buildOptions();

        if (this.basicLabel) {
            this.floatLabel = false;
            this.innerLabel = false;
        }

        if (this.hideLabel) {
            this.labelClass += ` ${ClassNames.ScreenReaderOnly}`;
        }

        if (this.innerLabel || this.innerLeftLabel) {
            this.floatLabel = false;
        }

        if (this.disabled) {
            this.handleBlur();
        }

        if (this.isNullable && this.options[0] && this.options[0].value != null) {
            this.options.unshift({
                label: '',
                value: undefined
            });
        }

        if (this.border === 'light' && this.inputClass.indexOf(ClassNames.BackgroundDark) === -1) {
            this.viewportClass += ` ${ClassNames.BorderLight}`;
        } else {
            this.viewportClass += ` ${ClassNames.BorderDark}`;
        }

        if ((this.group && this.formControlName) || this.control) {
            const formControl = this.getControl();
            const formControlValue = formControl.value;
            this.selectedOptionIndex = this.options.findIndex(
                (option) => option.value === formControlValue
            );
        }

        this.handleSelectChange();

        // Allows us to update the state of our select box when `ngModel` changes,
        // as if we had a native <select> inside our component.
        this.modelChangeSubject.subscribe((value) => {
            this.model = value;
            this.selectedOptionIndex = this.options.findIndex(
                (o) => isEqual(o, this.model) || isEqual(o.value, this.model)
            );
            this.handleSelectChange(null, false);
        });
    }

    private buildOptions(): void {
        const newOptions: ISelectOption[] = [];

        function recursivelyAddOptions(options: ISelectOption[], areChildren?: boolean): void {
            options.forEach((option) => {
                if (areChildren) {
                    option.isChild = true;
                }
                newOptions.push(option);
                if (option.children) {
                    recursivelyAddOptions([...option.children], true);
                    delete option.children;
                }
            });
        }

        // Create SelectOption objects out of string or number options.
        this.options = this.options.map(
            (option: any): ISelectOption => {
                if (typeof option === 'object' && option !== null) {
                    return <ISelectOption>option;
                } else {
                    let label = option;
                    if (typeof option === 'number') {
                        label = option.toString();
                    }
                    return {
                        label,
                        value: option
                    };
                }
            }
        );

        // Pull child options out into the options array.
        recursivelyAddOptions(this.options);
        this.options = [...newOptions];
    }

    public doSearch(event: KeyboardEvent): void {
        event.stopPropagation();
        if (this.clearSearchTimer) {
            clearTimeout(this.clearSearchTimer);
        }
        this.clearSearchTimer = <any>setTimeout(() => {
            this.clearSearch();
        }, 1000);
        this.search += event.key.toLowerCase();
        for (let index = 0; index < this.options.length; index++) {
            const option = this.options[index];
            const labelToSearch = option.label;
            const search = escapeRegExp(this.search);
            let valueToSearch =
                typeof option.value === 'number' ? option.value.toString() : option.value;
            if (typeof valueToSearch !== 'string') {
                valueToSearch = labelToSearch;
            }
            if (
                labelToSearch.match(new RegExp(`^${search}`, 'i')) ||
                valueToSearch.match(new RegExp(`^${search}`, 'i'))
            ) {
                this.setHighlightedOption(
                    index,
                    this.dropdownMenuRef,
                    this.optionRefs,
                    this.scrollRef
                );
                this.handleSelectChange(index, true);
                break;
            }
        }
    }

    public clearSearch(): void {
        this.search = '';
    }

    public handleSelectChange(index?: number, propagate = true, collapse = false): void {
        if (index != null) {
            this.selectedOptionIndex = index;
        }
        if (collapse) {
            this.isExpanded = false;
            // Blur the element so that the green border goes away.
            this.selectElement.nativeElement.blur();
        }

        if (propagate) {
            this.handleChange(
                this.options[this.selectedOptionIndex]
                    ? this.options[this.selectedOptionIndex].value
                    : undefined
            );
        }
    }

    // This will set the 'is-focused-or-filled' class to style float labels or inner labels, but for selects, we really only want to test the 'isFilled' part. For simplicity's sake, we've kept the class name the same.
    public isFilledClass(): string {
        const isFilled = (): boolean => {
            if (this.selectedOptionIndex > -1) {
                if (this.isNullable) {
                    return this.selectedOptionIndex > 0;
                } else {
                    return true;
                }
            } else if (this.model && !!this.model.length) {
                return true;
            } else {
                return false;
            }
        };

        return isFilled() ? ClassNames.IsFocusedOrFilled : '';
    }

    public handleViewportKeypress(event: KeyboardEvent): void {
        if (this.disabled) {
            return;
        }
        if (event.which === KeyCodes.UpArrow) {
            event.preventDefault();
            if (!this.highlightedOptionIndex) {
                this.setHighlightedOption(
                    this.options.length - 1,
                    this.dropdownMenuRef,
                    this.optionRefs,
                    this.scrollRef
                );
            } else {
                this.setHighlightedOption(
                    this.highlightedOptionIndex - 1,
                    this.dropdownMenuRef,
                    this.optionRefs,
                    this.scrollRef
                );
            }
            this.handleSelectChange(null, false);
        } else if (event.which === KeyCodes.DownArrow) {
            event.preventDefault();
            if (!this.isExpanded) {
                this.isExpanded = true;
            } else if (
                typeof this.highlightedOptionIndex === 'undefined' ||
                this.highlightedOptionIndex === this.options.length - 1
            ) {
                this.setHighlightedOption(0, this.dropdownMenuRef, this.optionRefs, this.scrollRef);
            } else {
                this.setHighlightedOption(
                    this.highlightedOptionIndex + 1,
                    this.dropdownMenuRef,
                    this.optionRefs,
                    this.scrollRef
                );
            }
            this.handleSelectChange(null, false);
        } else if (
            (event.which === KeyCodes.Enter || event.which === KeyCodes.Space) &&
            this.isExpanded
        ) {
            event.preventDefault();
            this.selectedOptionIndex = this.highlightedOptionIndex;
            this.toggleOptionsDropdown();
        } else if (event.which === KeyCodes.Space && !this.isExpanded) {
            event.preventDefault();
            this.toggleOptionsDropdown();
            this.highlightedOptionIndex = this.selectedOptionIndex;
        } else if (event.which === KeyCodes.Escape) {
            event.preventDefault();
            this.toggleOptionsDropdown(false);
        }
    }

    public createId(): string {
        if (this.name) {
            return this.name;
        }
        if (!this.label) {
            return '';
        }
        return this.label.replace(/\s/g, '-').toLowerCase();
    }

    public toggleOptionsDropdown(expanded?: boolean): void {
        if (this.disabled) {
            return;
        }
        const wasExpanded = this.isExpanded;
        delete this.highlightedOptionIndex;
        this.isExpanded = typeof expanded === 'undefined' ? !this.isExpanded : expanded;
        if (this.isExpanded === false && wasExpanded) {
            this.handleSelectChange();
        }
    }

    public displayNullOptionSelected(): boolean {
        return this.hideLabel && this.isNullable && this.selectedOptionIndex === 0;
    }
    public displaySelectedOption(): boolean {
        return !this.isNullable || this.selectedOptionIndex > 0;
    }

    public isNullOption(i: number): boolean {
        return i === 0 && this.isNullable;
    }
    public isNotNullOption(i: number): boolean {
        return !this.isNullOption(i);
    }

    public hasDarkBackground(): boolean {
        return this.parentClass.indexOf(ClassNames.BackgroundDark) > -1;
    }

    public handleFocus(): void {
        this.isFocused = true;
    }

    public handleBlur(): void {
        Utilities.executeAfterRender(() => {
            this.isFocused = false;
            this.clearSearch();
            this.isExpanded = false;
            delete this.highlightedOptionIndex;
            this.blur.emit();
        });
    }

    // Called directly from FormGroup
    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
